import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Plus } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  listInventories,
  listInventoryOrganizations,
  listInventoryWarehouses,
  startInventory,
  type RefName,
} from "@/api/inventory";
import { Button } from "@/components/ui/button";
import { RefSelect } from "@/components/RefSelect";
import { Card } from "@/components/ui/card";
import { fmtDate } from "@/lib/format";
import { apiErrorText } from "@/lib/scan-ui/screen";

/**
 * Инвентаризации: список и старт.
 *
 * Организация и склад выбираются ЯВНО и оба уезжают в шапку смены: «что мы
 * вообще инвентим» — первое, что перестаёт быть очевидным через час работы,
 * а ошибка в этом месте стоит документа по чужому складу.
 */
export function InventoryListPage(): React.ReactElement {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [adding, setAdding] = React.useState(false);
  const [orgRef, setOrgRef] = React.useState("");
  const [whRef, setWhRef] = React.useState("");
  // «Начать» нажимается всегда: погашенная кнопка без объяснения читалась как
  // «ничего не происходит». Попытка без выбора подсвечивает, чего не хватает.
  const [tried, setTried] = React.useState(false);
  const [comment, setComment] = React.useState("");

  const list = useQuery({ queryKey: ["inventories"], queryFn: () => listInventories() });
  const orgs = useQuery({
    queryKey: ["inventory-orgs"],
    queryFn: () => listInventoryOrganizations(),
    enabled: adding,
    staleTime: 5 * 60_000,
  });
  const whs = useQuery({
    queryKey: ["inventory-warehouses"],
    queryFn: () => listInventoryWarehouses(),
    enabled: adding,
    staleTime: 5 * 60_000,
  });

  // Юрлицо одно — выбирать нечего, ставим сразу (после загрузки списка).
  React.useEffect(() => {
    const only = orgs.data?.length === 1 ? orgs.data[0] : undefined;
    if (only && !orgRef) setOrgRef(only.ref);
  }, [orgs.data, orgRef]);

  const org: RefName | null = orgs.data?.find((o) => o.ref === orgRef) ?? null;
  const wh: RefName | null = whs.data?.find((w) => w.ref === whRef) ?? null;

  const start = useMutation({
    mutationFn: (v: { org: RefName; wh: RefName }) =>
      startInventory({
        organization_ref: v.org.ref,
        organization_name: v.org.name,
        warehouse_ref: v.wh.ref,
        warehouse_name: v.wh.name,
        comment: comment.trim(),
      }),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ["inventories"] });
      navigate(`/inventory/sessions/${d.id}`);
    },
    onError: (e) => toast.error(apiErrorText(e)),
  });

  return (
    <div className="mx-auto max-w-2xl space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Инвентаризация</h1>
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="mr-1 size-4" />
            Начать
          </Button>
        )}
      </div>

      {adding && (
        <Card className="space-y-3 p-3">
          <RefSelect
            label="Организация"
            value={orgRef}
            onChange={setOrgRef}
            options={orgs.data}
            invalid={tried && !org}
            missingText="Выберите юр. лицо"
          >
            {orgs.isLoading && <p className="text-xs text-muted-foreground">загрузка…</p>}
            {/* Ошибку показываем ТЕКСТОМ: пустой список неотличим от
                «ничего не настроено», и раздел выглядел бы недоделанным,
                когда на деле не отвечает 1С или падает ручка. */}
            {orgs.isError && (
              <p className="text-xs text-red-600">
                Организации не загрузились: {apiErrorText(orgs.error)}
              </p>
            )}
            {/* Пусто — это НЕ «организаций нет», а «раздел не настроен». */}
            {orgs.data?.length === 0 && (
              <p className="text-xs text-amber-700">
                Юрлица раздела не настроены. Попросите администратора выбрать их в
                системных настройках.
              </p>
            )}
          </RefSelect>

          <RefSelect
            label="Склад"
            value={whRef}
            onChange={setWhRef}
            options={whs.data}
            invalid={tried && !wh}
            missingText="Выберите склад"
          >
            {whs.isLoading && <p className="text-xs text-muted-foreground">загрузка…</p>}
            {whs.isError && (
              <p className="text-xs text-red-600">
                Склады не загрузились: {apiErrorText(whs.error)}
              </p>
            )}
            {/* Пусто — это НЕ «складов нет», а «раздел не настроен». Молча
                показать все склады базы значило бы подменить настройку. */}
            {whs.data?.length === 0 && (
              <p className="text-xs text-amber-700">
                Склады раздела не настроены. Попросите администратора выбрать их в
                системных настройках — инвентаризовать все склады базы подряд не нужно.
              </p>
            )}
          </RefSelect>

          <label className="block text-xs text-muted-foreground">
            Комментарий (уедет в документ 1С)
            <input
              className="mt-1 w-full rounded border p-2 text-sm"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="напр. плановая, зона А"
            />
          </label>

          <p className="text-[11px] text-muted-foreground">
            При старте портал снимет учётные остатки склада — дальше они не меняются,
            чтобы движения за время пересчёта не выглядели расхождением.
          </p>

          <div className="flex gap-2">
            <Button
              className="flex-1"
              disabled={start.isPending}
              onClick={() => {
                setTried(true);
                if (!org || !wh) return;
                start.mutate({ org, wh });
              }}
            >
              {start.isPending ? "Снимаем остатки…" : "Начать инвентаризацию"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setAdding(false);
                setTried(false);
              }}
            >
              Отмена
            </Button>
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {list.data?.map((s) => (
          <button
            key={s.id}
            onClick={() => navigate(`/inventory/sessions/${s.id}`)}
            className="flex w-full items-center gap-2 rounded-lg border bg-card p-3 text-left"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{s.warehouse_name}</div>
              <div className="truncate text-xs text-muted-foreground">
                {s.organization_name} · {fmtDate(s.started_at)}
                {s.one_c_doc_number ? ` · ${s.one_c_doc_number}` : ""}
              </div>
              <div className="text-xs text-muted-foreground">
                посчитано {s.positions_counted} из {s.positions_total} · сканов {s.scans_total}
              </div>
            </div>
            <span
              className={
                "shrink-0 rounded px-1.5 py-0.5 text-[10px] " +
                (s.status === "open"
                  ? "bg-amber-100 text-amber-900"
                  : "bg-emerald-100 text-emerald-800")
              }
            >
              {s.status === "open" ? "идёт" : "завершена"}
            </span>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          </button>
        ))}
        {list.data?.length === 0 && !adding && (
          <p className="text-sm text-muted-foreground">
            Инвентаризаций пока нет. Нажмите «Начать» и выберите склад.
          </p>
        )}
      </div>
    </div>
  );
}
